import { createContext, useContext } from 'react';
import type {
  SwimlaneRootSlotProps,
  SwimlaneSlotComponents,
  SwimlaneSlotComponentsProps,
} from '../types/slots';

/**
 * Context for storing and accessing Swimlane render slots.
 *
 * This context uses a base type for slot props and components.
 *
 * This allows deeply nested components to access render slots without prop drilling
 * while maintaining type safety through TypeScript's structural typing.
 */
export const SwimlaneSlotsContext = createContext<SwimlaneRootSlotProps | undefined>(undefined);

type UseSwimlaneSlotReturn<T extends keyof SwimlaneSlotComponents> = {
  component: SwimlaneSlotComponents[T];
  props: SwimlaneSlotComponentsProps[T];
};

export const useSwimlaneSlot = <T extends keyof SwimlaneSlotComponents>(
  slotName: T,
): UseSwimlaneSlotReturn<T> => {
  const context = useContext(SwimlaneSlotsContext);
  if (context === undefined) {
    throw new Error('useSwimlaneSlot must be used within a SwimlaneSlotsContext provider');
  }
  const { slots, slotProps } = context;
  return {
    component: (slots || ({} as SwimlaneSlotComponents))[slotName],
    props: slotProps?.[slotName] || {},
  };
};
